import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldAlert, KeyRound, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StepUpGate = () => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [caps, setCaps] = useState({ passkey: false, totp: false });
  const queueRef = useRef([]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      (error) => {
        const d = error.response?.data?.detail;
        if (error.response?.status === 403 && d?.code === "stepup_required" && !error.config.__stepupRetry) {
          return new Promise((resolve, reject) => {
            queueRef.current.push({ config: { ...error.config, __stepupRetry: true }, resolve, reject });
            setOpen(true);
          });
        }
        if (error.response?.status === 403 && d?.code === "lockdown_active") {
          toast.error(d.message, { id: "lockdown" });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    axios.post(`${API}/auth/stepup/options`).then(r =>
      setCaps({ passkey: r.data.passkey, totp: r.data.totp, options: r.data.options })
    ).catch(() => {});
  }, [open]);

  const replay = async () => {
    const q = queueRef.current; queueRef.current = [];
    setOpen(false); setCode("");
    for (const item of q) {
      try { item.resolve(await axios.request(item.config)); }
      catch (e) { item.reject(e); }
    }
  };

  const cancel = () => {
    queueRef.current.forEach(i => i.reject(new Error("Step-up cancelled")));
    queueRef.current = [];
    setOpen(false); setCode("");
  };

  const viaPasskey = async () => {
    setBusy(true);
    try {
      const { startAuthentication } = await import("@simplewebauthn/browser");
      const { data } = await axios.post(`${API}/auth/stepup/options`);
      const assertion = await startAuthentication({ optionsJSON: data.options });
      await axios.post(`${API}/auth/stepup/verify`, assertion);
      toast.success("Identity confirmed (10 min window)");
      await replay();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Passkey confirmation failed");
    } finally { setBusy(false); }
  };

  const viaTotp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/auth/stepup/totp`, { code });
      toast.success("Identity confirmed (10 min window)");
      await replay();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Invalid code");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); }}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-sm" data-testid="stepup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="w-5 h-5 text-amber-400" /> Confirm it's you
          </DialogTitle>
          <DialogDescription>
            This is a sensitive action. Confirm with your passkey or authenticator code.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {caps.passkey && (
            <Button onClick={viaPasskey} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="stepup-passkey-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><KeyRound className="w-4 h-4 mr-2" /> Use passkey</>)}
            </Button>
          )}
          {caps.totp && (
            <div className="flex gap-2">
              <Input value={code} onChange={e => setCode(e.target.value)} placeholder="6-digit code" maxLength={6}
                     className="bg-black/50 border-white/10 text-white text-center font-mono" data-testid="stepup-totp-input" />
              <Button onClick={viaTotp} disabled={busy || code.length !== 6} className="bg-purple-600 hover:bg-purple-700" data-testid="stepup-totp-btn">Verify</Button>
            </div>
          )}
          {!caps.passkey && !caps.totp && (
            <p className="text-sm text-zinc-400">No passkey or 2FA found — add one in Settings → Security first.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
